import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

import IncidentValidationSchema from './IncidentValidationSchema';

import { Incident, IncidentStatus } from '../../core/api/clients';

import ControlledForm from '../../components/ControlledForm';

import IncidentFormBanner from '../../components/incident/IncidentFormBanner';
import IncidentFormBody from '../../components/incident/IncidentFormBody';

import { useIncidentReportData } from '../../core/context/IncidentReportDataContext';
import { FormValidationProvider } from '../../core/context/FormValidationContext';

function IncidentFormView(): React.JSX.Element {
  const { incidentReport, ...incidentReportMethods } = useIncidentReportData();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { formState, ...formMethods } = useForm<Incident>({ defaultValues: incidentReport });

  React.useEffect(() => {
    formMethods.reset(incidentReport);
  }, [incidentReport]);

  const onSubmit = () => enqueueSnackbar('This form is read-only', { variant: 'error' });
  const handleReopen = (reopenReason: string) => {
    formMethods.setValue('reopenReason', reopenReason);
    formMethods.setValue('status', IncidentStatus.Open);

    const reopenedIncident = formMethods.getValues();

    incidentReportMethods.update(reopenedIncident, () => {
      navigate(`/form/incident/${incidentReport.id}/edit`);
    });
  };

  const onError = () => null;

  return (
    <ControlledForm
      enableUpdate={incidentReport.status === IncidentStatus.Open}
      formProps={{
        noValidate: true,
      }}
      onSubmit={formMethods.handleSubmit(onSubmit, onError)}
    >
      <FormProvider formState={formState} {...formMethods}>
        <FormValidationProvider<Incident>>
          <IncidentFormBanner
            incident={incidentReport}
            saveButtonProps={{ isHidden: true }}
          />
          <IncidentFormBody incident={incidentReport} onReopen={handleReopen} readOnly onDelete={() => {}} />
        </FormValidationProvider>
      </FormProvider>
    </ControlledForm>
  );
}

export default IncidentFormView;
